import {
  ApolloLink,
  type FetchResult,
  type NextLink,
  Observable,
  type Operation,
} from "@apollo/client";
import { print } from "graphql";
import localforage from "localforage";

/**
 * Offline mutation queue — port of the legacy OfflineLink behaviour.
 *
 * When the browser is offline, mutations tagged `context.offline === true` are
 * persisted to localforage and their `optimisticResponse` is returned
 * immediately, so the UI updates as if the write succeeded. On reconnect the
 * queue is replayed sequentially (preserving order) against the real server.
 *
 * Queries are never queued — they fall through to the next link and rely on the
 * persisted Apollo cache for offline reads.
 *
 * Note: file uploads (createPost fotos) need ArrayBuffer (de)serialization
 * before they can be queued; that is wired alongside the upload link.
 */

interface QueuedOperation {
  query: string;
  variables: Record<string, unknown>;
  operationName: string | null;
}

const queueStore = localforage.createInstance({ name: "thruhiker", storeName: "operations" });
const QUEUE_KEY = "queue";

async function readQueue(): Promise<QueuedOperation[]> {
  return (await queueStore.getItem<QueuedOperation[]>(QUEUE_KEY)) ?? [];
}

async function writeQueue(queue: QueuedOperation[]): Promise<void> {
  await queueStore.setItem(QUEUE_KEY, queue);
}

function isMutation(operation: Operation): boolean {
  return operation.query.definitions.some(
    (d) => d.kind === "OperationDefinition" && d.operation === "mutation",
  );
}

export class OfflineQueueLink extends ApolloLink {
  override request(operation: Operation, forward?: NextLink): Observable<FetchResult> | null {
    const offlineEligible =
      isMutation(operation) && operation.getContext().offline === true;

    if (offlineEligible && typeof navigator !== "undefined" && !navigator.onLine) {
      // Persist for later replay, resolve with the optimistic response now.
      void this.enqueue(operation);
      const optimistic = operation.getContext().optimisticResponse;
      return new Observable<FetchResult>((observer) => {
        observer.next({ data: optimistic ?? null });
        observer.complete();
      });
    }

    return forward ? forward(operation) : null;
  }

  private async enqueue(operation: Operation): Promise<void> {
    const queue = await readQueue();
    queue.push({
      query: print(operation.query),
      variables: operation.variables,
      operationName: operation.operationName ?? null,
    });
    await writeQueue(queue);
  }
}

/**
 * Replay queued mutations against the GraphQL endpoint, oldest first. Stops on
 * the first network failure (stays offline) and keeps the remainder queued.
 * Returns the number of operations successfully flushed.
 */
export async function flushQueue(uri: string, authToken: string | null): Promise<number> {
  const queue = await readQueue();
  let flushed = 0;

  for (const op of queue) {
    try {
      const res = await fetch(uri, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          query: op.query,
          variables: op.variables,
          operationName: op.operationName,
        }),
      });
      if (!res.ok) break; // network/server issue — retry later
      flushed++;
    } catch {
      break; // still offline
    }
  }

  if (flushed > 0) {
    await writeQueue(queue.slice(flushed));
  }
  return flushed;
}

export async function pendingCount(): Promise<number> {
  return (await readQueue()).length;
}
